import {
  useEffect,
  useRef,
  useCallback,
  ReactNode,
  RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { IconButton } from '@/components/common/Button';
import FaIcon from '@/components/common/FaIcon';

// =============================================================================
// STANDARDIZED MODAL COMPONENT
// =============================================================================
// Accessible dialog with focus trap, Escape key handling, backdrop click,
// and focus restoration. Renders via React Portal to document.body.

type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

const SIZE_MAP: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  full: 'max-w-[95vw]',
};

const FOCUSABLE_SELECTORS = [
  'a[href]',
  'area[href]',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'button:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  'details > summary',
].join(', ');

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Title text or JSX. Optional when `hideHeader` is set (children supply their own chrome). */
  title?: ReactNode;
  /** Secondary line rendered under the title. */
  subtitle?: ReactNode;
  /** Icon rendered before the title. */
  icon?: ReactNode;
  /** Extra content in the header, below the title row (e.g. a tab bar). */
  headerContent?: ReactNode;
  /** Hide the default header entirely; children render their own header + close affordance. */
  hideHeader?: boolean;
  size?: ModalSize;
  children: ReactNode;
  footer?: ReactNode;
  /** Class applied to the scrollable body wrapper (e.g. 'p-6' to pad). Default unpadded. */
  bodyClassName?: string;
  /** Accessible label when no visible `title` is rendered. */
  ariaLabel?: string;
  /**
   * Replaces the panel's *presentation* classes — the rounded surface, border
   * and shadow — while structure (sizing, flex column, focus outline) stays.
   *
   * This exists because bespoke overlays (a lightbox, a full-bleed viewer) were
   * being hand-rolled from scratch to get a different look, and losing the
   * focus trap, the inert background and Escape along with it. Skin it here
   * instead of rebuilding it there.
   */
  panelClassName?: string;
  closeOnBackdrop?: boolean;
  closeOnEsc?: boolean;
  initialFocusRef?: RefObject<HTMLElement>;
  'aria-describedby'?: string;
}

export default function Modal({
  isOpen,
  onClose,
  title,
  subtitle,
  icon,
  headerContent,
  hideHeader = false,
  size = 'md',
  children,
  footer,
  bodyClassName = '',
  panelClassName,
  ariaLabel,
  closeOnBackdrop = true,
  closeOnEsc = true,
  initialFocusRef,
  'aria-describedby': ariaDescribedBy,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useRef(`modal-title-${Math.random().toString(36).slice(2)}`).current;
  const bodyRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Record the trigger DURING RENDER, not in an effect.
  //
  // A child with `autoFocus` takes focus during commit, which happens before
  // effects run. Reading document.activeElement in the effect therefore
  // recorded that child rather than the element that opened the modal — and on
  // close the restore aimed at a node that had just been unmounted, hit the
  // `isConnected` guard, and silently did nothing. Focus landed on <body>.
  // Render runs before commit, so this sees the real trigger.
  if (isOpen && previousFocusRef.current === null) {
    previousFocusRef.current = document.activeElement as HTMLElement;
  }

  // Apply inert to siblings so screen readers cannot reach background content.
  // NOTE: defined BEFORE the focus effect on purpose — cleanups run in
  // definition order, so on close/unmount the inert attribute is removed
  // before focus is restored (focus() is a no-op on inert elements).
  useEffect(() => {
    if (!isOpen) return;

    const siblings: Element[] = [];
    document.body.childNodes.forEach((node) => {
      if (
        node instanceof HTMLElement &&
        node !== document.getElementById('modal-portal-root') &&
        // The toast layer is never background: it is how a failed save says so,
        // and a toast already on screen when this modal opened would otherwise
        // be frozen out of the accessibility tree for the rest of its life.
        !node.hasAttribute('data-toast-layer') &&
        !node.contains(panelRef.current)
      ) {
        // Only inert siblings that don't already have the attribute
        if (!node.hasAttribute('inert')) {
          node.setAttribute('inert', '');
          siblings.push(node);
        }
      }
    });

    return () => {
      siblings.forEach((el) => el.removeAttribute('inert'));
    };
  }, [isOpen]);

  // Focus management: move focus INTO the modal on open; RETURN it to the
  // trigger on close. Restoration happens in the cleanup so it also covers
  // the common `{open && <Modal …/>}` pattern where the component unmounts
  // while still open.
  useEffect(() => {
    if (!isOpen) return;

    // Defer focus so the portal has rendered -- but do not depend on a frame
    // ever arriving.
    //
    // requestAnimationFrame does not run in a hidden tab. Measured against
    // production on 2026-08-26 in a background tab: the dialog opened, was
    // correctly built, and focus stayed on <body> because the callback never
    // fired. That is a real arrangement here, not just a test artefact -- EOC
    // wall displays park pages in background tabs, and socket events and
    // keyboard shortcuts both open modals. The operator switches back to a
    // dialog nothing has focused, which is the same dead end as UI-100.
    //
    // A timeout runs in hidden tabs (throttled, but it runs), so whichever
    // arrives first does the work and the other becomes a no-op.
    let done = false;
    const focusIn = () => {
      if (done) return;
      done = true;
      if (initialFocusRef?.current) {
        initialFocusRef.current.focus();
      } else if (panelRef.current?.contains(document.activeElement)) {
        // A child already claimed focus — an `autoFocus` search box, say.
        // Overriding it would drop the caller on whatever happens to come
        // first in the DOM, which is usually the close button.
      } else if (panelRef.current) {
        // Prefer the first *field* in the body.
        //
        // The close button is the first focusable element in the panel, so the
        // plain "first focusable" rule landed every modal that expresses no
        // preference — 176 of the 204 callers — on Close. For a data-entry
        // modal that is the one control the operator does not want: it is the
        // way out, not the way in, and Enter on it discards the dialog they
        // just opened.
        //
        // Deliberately fields only, never buttons. Focusing whatever comes
        // first in the body would sometimes mean a Delete, and startling
        // someone with the destructive control under their cursor is worse
        // than the problem being fixed. With no field, behaviour is unchanged.
        const firstField = bodyRef.current?.querySelector<HTMLElement>(
          'input:not([disabled]):not([type="hidden"]):not([type="checkbox"]):not([type="radio"]), select:not([disabled]), textarea:not([disabled])'
        );
        // With no field, focus the panel -- never the close button.
        //
        // The old fallback was "first focusable in the panel", and the close
        // button is always first in DOM order, so every field-less modal opened
        // with the dismiss control armed under Enter. Seen live on the Tasks
        // wizard, 2026-08-26: step 1 is six task-type cards and focus landed on
        // the X.
        //
        // Focusing the panel instead announces the dialog and its content from
        // the top, leaves Tab to reach the controls in the order they are
        // written, and arms nothing -- which is also why the delete
        // confirmations, already verified to behave well, land here.
        if (firstField) {
          firstField.focus();
        } else {
          panelRef.current.focus();
        }
      }
    };

    const frame = requestAnimationFrame(focusIn);
    const timer = setTimeout(focusIn, 50);

    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timer);
      if (previousFocusRef.current) {
        // `=== document.body` is the same failure wearing a different hat: the
        // modal was opened with nothing focused (a route, a shortcut, a toast
        // action), so "restoring" hands focus back to a dead end.
        if (
          previousFocusRef.current.isConnected &&
          previousFocusRef.current !== document.body
        ) {
          previousFocusRef.current.focus();
        } else {
          // The trigger no longer exists. This is the ordinary outcome of a
          // delete: the confirmation was opened from the row's own Delete
          // button, and confirming removed that row. Declining to focus a
          // detached node is correct, but stopping there drops focus onto
          // <body>, which ends keyboard navigation and leaves a screen reader
          // with nowhere to resume -- the next Tab restarts from the top of the
          // document, back through the whole sidebar.
          //
          // The main landmark is the one destination that always exists and is
          // always the right neighbourhood: it contains the list the row was
          // removed from. preventScroll keeps the list where the user left it
          // rather than snapping to the top of the page.
          document
            .querySelector<HTMLElement>('main[tabindex="-1"]')
            ?.focus({ preventScroll: true });
        }
        previousFocusRef.current = null;
      }
    };
  }, [isOpen, initialFocusRef]);

  // Focus trap: Tab / Shift+Tab cycles within the panel
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape' && closeOnEsc) {
        // An Escape a descendant already handled is not ours to act on.
        //
        // Typeaheads inside a modal -- AgencyPicker, ContactPicker,
        // ContactAutocompleteField, TemplateVariableAutocomplete -- close their
        // suggestion list on Escape and call preventDefault to say so. Nothing
        // read that, so the keypress carried on to here and closed the dialog
        // too: dismissing a suggestion threw away the half-filled form beneath
        // it. `defaultPrevented` is the standard "this event has been handled"
        // signal, so honouring it fixes every such child at once, including
        // ones not written yet.
        if (e.defaultPrevented) return;
        e.stopPropagation();
        onClose();
        return;
      }

      if (e.key !== 'Tab' || !panelRef.current) return;

      const focusableElements = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS)
      ).filter((el) => !el.closest('[inert]'));

      if (focusableElements.length === 0) return;

      const first = focusableElements[0];
      const last = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    [closeOnEsc, onClose]
  );

  /**
   * A backdrop click must not discard typing.
   *
   * 146 form-bearing modals in this app close on an outside click with nothing
   * in the way. On a touch screen, in an EOC, one stray tap beside a
   * half-finished damage assessment or a 19-field mutual-aid agreement throws
   * the lot away with no warning and no undo. Setting closeOnBackdrop={false}
   * in 146 places would fix it for exactly as long as nobody adds the 147th.
   *
   * So the primitive notices instead: any input/change event inside the panel
   * marks it dirty, and a dirty modal ignores backdrop clicks. Escape and
   * Cancel still work — those are deliberate, a stray tap is not. An explicit
   * closeOnBackdrop={false} still wins outright.
   */
  const dirtyRef = useRef(false);

  const handleBackdropClick = useCallback(() => {
    if (!closeOnBackdrop) return;
    if (dirtyRef.current) return;
    onClose();
  }, [closeOnBackdrop, onClose]);

  if (!isOpen) return null;

  const panel = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overscroll-contain p-4"
      onKeyDown={handleKeyDown}
    >
      {/* Backdrop — standardized scrim via the shared overlay token.
          Entrance only (animate-fade-in); close stays instant per motion doctrine. */}
      <div
        className="absolute inset-0 bg-[color:var(--surface-overlay)] animate-fade-in"
        aria-hidden="true"
        onClick={handleBackdropClick}
      />

      {/* Panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={!title ? ariaLabel : undefined}
        aria-describedby={ariaDescribedBy}
        tabIndex={-1}
        className={`relative w-full ${SIZE_MAP[size]} overlay-max-h-90 flex flex-col outline-none animate-scale-in ${
          panelClassName ?? 'rounded-lg bg-surface-elevated border border-theme-primary shadow-xl'
        }`}
        onClick={(e) => e.stopPropagation()}
        onInput={() => {
          dirtyRef.current = true;
        }}
        onChange={() => {
          dirtyRef.current = true;
        }}
      >
        {/* Header */}
        {!hideHeader && (
          <div className="border-b border-theme-primary px-6 py-4 flex-shrink-0">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                {icon && (
                  <span className="flex-shrink-0 text-primary-600 dark:text-primary-400">{icon}</span>
                )}
                <div className="min-w-0">
                  {title && (
                    <h2 id={titleId} className="heading-3 truncate">
                      {title}
                    </h2>
                  )}
                  {subtitle && <p className="mt-0.5 text-sm text-theme-tertiary">{subtitle}</p>}
                </div>
              </div>
              <IconButton
                onClick={onClose}
                icon={<FaIcon icon="xmark" className="h-5 w-5" />}
                label="Close dialog"
                variant="ghost"
              />
            </div>
            {headerContent && <div className="mt-3">{headerContent}</div>}
          </div>
        )}

        {/* Body */}
        {/* `overscroll-contain`: scrolling past the end of a modal's body must
            not chain into the page behind it. Nothing locks body scroll while
            a modal is open -- doing that safely on iOS needs the
            position-fixed/restore-scrollTop dance, and getting it wrong makes
            the page jump on close -- so containing the chain is what stops the
            background moving under the overlay. */}
        <div ref={bodyRef} className={`flex-1 overflow-y-auto overscroll-contain ${bodyClassName}`}>
          {children}
        </div>

        {/* Footer (optional) */}
        {footer && (
          <div className="flex-shrink-0 border-t border-theme-primary px-6 py-4">
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(panel, document.body);
}
